/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ReuseAvailability } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/ReuseAvailability';
import { RootState } from 'app/AppStore';
import { FilterIcon, RecycleIcon } from 'assets/icons';

import { useDisclosure } from 'components/Dialog';
import { Pagination } from 'components/Pagination';
import { ReferencePriceSection } from 'components/ReferencePriceSection';
import { getExplodedTree, getExplodedTreeItem, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { DATAHUB, STANDARD } from 'domains/catalog/Catalog.types';
import { getDHReference, getPrice } from 'domains/references';
import { getSparePartsView } from 'domains/user/User.store';
import { ClientView } from 'domains/user/User.types';
import { BrandImage } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/BrandImage';
import ReuseAvailabilitiesFilters, {
  applyReuseFilters,
  defaultReuseFilters,
  getReusePriceBounds,
  ReuseFilters,
} from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReuseAvailabilities/ReuseAvailabilitiesFilters';
import ReusePartCard from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReuseAvailabilities/ReusePartCard';
import ReuseProductPage from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReuseAvailabilities/ReuseProductPage';
import { theme } from 'styles';
import { Flex, Icon, MarginBox, Text } from 'UI';
import { Drawer } from 'UI/Drawer';
import ViewTabs from 'UI/Tabs/ViewTabs';
import { getData, useBreakpointSelectorFull } from 'utils';
import { SFilterTrigger, SListView, SModal, SStickyHeader, SStickyIdRefBox } from './ReuseAvailabilities.styled';

const PAGE_SIZE = 4;
const DEFAULT_VAT_RATE = 20;

const toOptionalNumber = (value: string | number | undefined): number | undefined => {
  if (value === undefined || value === '') {
    return undefined;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

interface ReuseAvailabilitiesModalProps {
  open: boolean;
  onClose: () => void;
  referenceNumber: string;
  parts: ReuseAvailability[];
}

const ReuseAvailabilitiesModal = ({ open, onClose, referenceNumber, parts }: ReuseAvailabilitiesModalProps) => {
  const { t } = useTranslation();
  const view = useSelector(getSparePartsView);
  const { carPartGroupId, categoryId } = useParams<{ carPartGroupId?: string; categoryId?: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPart, setSelectedPart] = useState<ReuseAvailability | null>(null);
  const listTopRef = useRef<HTMLDivElement>(null);
  const filterDisclosure = useDisclosure();
  // On lg and below the filters collapse into a slide-in panel triggered by the filter icon.
  const showFilterDrawer = useBreakpointSelectorFull()({
    xs: true,
    sm: true,
    md: true,
    lg: true,
    xl: false,
    xml: false,
    xxl: false,
  });

  const vehicleDetail = useSelector(getLastVehicleDetail);
  const explodedTree = useSelector((state: RootState) => getExplodedTree(state, vehicleDetail?.vehicleKey));
  // Parent catalog category label for the product-page "Category" row (hidden when unavailable).
  const category = useMemo(
    () =>
      carPartGroupId && categoryId
        ? getExplodedTreeItem(explodedTree?.data, [carPartGroupId, categoryId])?.label
        : undefined,
    [explodedTree?.data, carPartGroupId, categoryId],
  );
  const detail = useSelector((state: RootState) =>
    getDHReference(state, { vehicleKey: vehicleDetail?.vehicleKey, referenceNumber }),
  );
  const referenceData = getData(detail);
  const price = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const priceData = getData(price);

  const vatRate = priceData?.garageView?.vatPercentage ?? DEFAULT_VAT_RATE;
  const newGaragePrice = toOptionalNumber(priceData?.garageView?.vatExcludedPrice);
  const newPcl = toOptionalNumber(priceData?.clientView?.recommendedPriceVatExcluded);

  const priceBounds = useMemo(() => getReusePriceBounds(parts), [parts]);
  const [filters, setFilters] = useState<ReuseFilters>(() => defaultReuseFilters(parts));

  // Reset the price range to the data bounds whenever the loaded parts change.
  useEffect(() => {
    setFilters((prev) => ({ ...prev, price: priceBounds }));
  }, [priceBounds]);

  // Go back to the first page whenever the active filters change.
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Scroll the results back to the top on page change so the new page starts at the first item.
  useEffect(() => {
    listTopRef.current?.closest('.ant5-modal-body')?.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const filteredParts = useMemo(() => applyReuseFilters(parts, filters), [parts, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredParts.length / PAGE_SIZE));
  const pagedParts = useMemo(() => filteredParts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE), [
    filteredParts,
    currentPage,
  ]);

  // Scroll to the top so the slide-in filter panel is fully in view when opened.
  const openFilterDrawer = () => {
    listTopRef.current?.closest('.ant5-modal-body')?.scrollTo?.({ top: 0, behavior: 'smooth' });
    filterDisclosure.onOpen();
  };

  // Reset to the list view so reopening the modal never starts on a stale product page.
  const handleClose = () => {
    setSelectedPart(null);
    onClose();
  };

  return (
    <SModal
      open={open}
      onCancel={handleClose}
      closable
      maskClosable
      footer={null}
      title={
        <SStickyHeader data-testid={'reuse-availabilities-modal-header'} gap={16}>
          <Flex align={'center'} gap={8} size={0}>
            <Icon IconComponent={RecycleIcon} size={44} color={theme.color.white} noPointer />
          </Flex>

          <Flex direction={'column'}>
            <Text type={'heading4'} color={'white'} bold disableGutter>
              {t('catalog.reuse_availabilities.modal_title', 'ReUse parts')}
            </Text>
            <Text
              type={'lg'}
              color={'white'}
              bold
              disableGutter
              data-testid={'reuse-availabilities-reference-designation'}
            >
              {referenceData?.name} {`${vehicleDetail?.vehicleBrand ?? ''} ${vehicleDetail?.model ?? ''}`.trim()}
              {vehicleDetail?.vin && ` - ${t('catalog.vehicle.vin', 'VIN')}: ${vehicleDetail.vin}`}
              {vehicleDetail?.vrn && ` - ${t('catalog.vehicle.vrn', 'VRN')}: ${vehicleDetail.vrn}`}
            </Text>
          </Flex>
        </SStickyHeader>
      }
    >
      {selectedPart ? (
        <Flex direction={'column'} padding={24}>
          <ReuseProductPage
            part={selectedPart}
            referenceNumber={referenceNumber}
            referenceName={referenceData?.name}
            category={category}
            vatRate={vatRate}
            newPcl={newPcl}
            newGaragePrice={newGaragePrice}
            onBack={() => setSelectedPart(null)}
          />
        </Flex>
      ) : (
        <SListView>
          <Flex direction={'row'} padding={24} gap={24}>
            {!showFilterDrawer && (
              <ReuseAvailabilitiesFilters filters={filters} onFiltersChange={setFilters} priceBounds={priceBounds} />
            )}
            <Flex direction={'column'}>
              <div ref={listTopRef} />
              {showFilterDrawer ? (
                <Flex direction={'row'} justify={'space-between'} align={'center'} gap={12}>
                  <SFilterTrigger
                    type={'button'}
                    data-testid={'reuse-availabilities-filter-trigger'}
                    onClick={openFilterDrawer}
                  >
                    <Icon IconComponent={FilterIcon} size={20} />
                  </SFilterTrigger>
                  <ViewTabs />
                </Flex>
              ) : (
                <ViewTabs />
              )}
              <SStickyIdRefBox data-testid={'reuse-availabilities-id-ref-box'}>
                <BrandImage vehicleBrand={vehicleDetail?.vehicleBrand} size={85} noPointer />
                <Flex direction={'column'}>
                  <Text type={'heading3'} bold disableGutter>
                    {referenceData?.name}
                  </Text>
                  <Text type={'base'} color={'grey35'} disableGutter data-testid={'reuse-availabilities-id-ref-number'}>
                    {t('common.reference_number.short', 'Ref: {{ referenceNumber }}', { referenceNumber })}
                  </Text>
                </Flex>
                <ReferencePriceSection
                  referenceNumber={referenceNumber}
                  sparePartsView={view}
                  handleAddToCartClick={() => null}
                  vehicleKey={vehicleDetail?.vehicleKey}
                  isApplicableToCurrentVehicle={referenceData?.isApplicableToCurrentVehicle ?? false}
                  align={'left'}
                  catalogSource={DATAHUB}
                  referenceType={STANDARD}
                  hideButtons
                />
              </SStickyIdRefBox>
              <MarginBox mt={15}>
                <Flex direction={'column'} gap={15}>
                  {pagedParts.map((part, index) => {
                    const cardKey =
                      part.reuseReference ?? `${part.manufacturerReference}-${(currentPage - 1) * PAGE_SIZE + index}`;
                    return view === ClientView ? (
                      <ReusePartCard
                        key={cardKey}
                        part={part}
                        view={'CLIENT'}
                        vatRate={vatRate}
                        newPcl={newPcl}
                        newGaragePrice={newGaragePrice}
                        onSelect={() => setSelectedPart(part)}
                      />
                    ) : (
                      <ReusePartCard
                        key={cardKey}
                        part={part}
                        view={'GARAGE'}
                        vatRate={vatRate}
                        onSelect={() => setSelectedPart(part)}
                      />
                    );
                  })}
                </Flex>
              </MarginBox>
              {totalPages > 1 && (
                <Pagination
                  total={filteredParts.length}
                  pageSize={PAGE_SIZE}
                  pageSizeOptions={[PAGE_SIZE]}
                  currentPage={currentPage}
                  onPageChange={(page) => setCurrentPage(page)}
                  showQuickJumper={false}
                  showPageSizeSelector={false}
                />
              )}
            </Flex>
          </Flex>
          {showFilterDrawer && (
            <Drawer
              title={t('catalog.reuse_availabilities.filters.title', 'Filters')}
              disclosure={filterDisclosure}
              placement={'left'}
              getContainer={false}
              width={290}
            >
              <ReuseAvailabilitiesFilters filters={filters} onFiltersChange={setFilters} priceBounds={priceBounds} />
            </Drawer>
          )}
        </SListView>
      )}
    </SModal>
  );
};

export default ReuseAvailabilitiesModal;
